import type React from 'react'
import { useState, useEffect } from 'react'

interface MenuItem {
  id: string | number
  title: React.ReactNode
  content: React.ReactNode
  className?: string
  activeClassName?: string
}

interface AutoSwitchProps {
  auto?: boolean
  items: MenuItem[]
  activeChange?: (index: number) => void
  interval?: number
  wrapperClassName?: string
  titleWrapperClassName?: string
  startActiveIndex?: number
  transitionDuration?: number
}

const AutoSwitch: React.FC<AutoSwitchProps> = ({
  auto = true,
  items,
  interval = 3000,
  startActiveIndex = 0,
  transitionDuration = 500,
  activeChange = undefined,
  titleWrapperClassName = '',
  wrapperClassName = '',
}) => {
  const [activeIndex, setActiveIndex] = useState(startActiveIndex)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (!auto || isPaused || items.length <= 1) return

    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % items.length)
    }, interval)

    return () => {
      clearInterval(timer)
    }
  }, [activeIndex, isPaused, interval, items.length])

  useEffect(() => {
    if (activeChange instanceof Function) {
      activeChange(activeIndex)
    }
  }, [activeIndex])

  useEffect(() => {
    setActiveIndex(startActiveIndex)
  }, [startActiveIndex])

  const handleMouseEnter = (index: number) => {
    setIsPaused(true)
    setActiveIndex(index)
  }

  const handleMouseLeave = () => {
    setIsPaused(false)
  }

  if (items.length === 0) return null

  return (
    <div
      className={
        wrapperClassName ||
        'flex gap-1 lg:gap-4 md:gap-3 sm:gap-2 mx-auto p-5 lg:flex-row md:flex-row sm:flex-col flex-col'
      }
    >
      {/* 左侧菜单容器 */}
      <div
        className={
          titleWrapperClassName ||
          'flex justify-center lg:flex-col md:flex-col sm:flex-row flex-row gap-1 lg:gap-4 md:gap-3 sm:gap-2'
        }
      >
        {items.map((item, index) => {
          const isActive = index === activeIndex
          return (
            <div
              key={item.id}
              onMouseEnter={() => {
                handleMouseEnter(index)
              }}
              onMouseLeave={handleMouseLeave}
              className={`flex items-center cursor-pointer transition-all duration-300 ${isActive ? item.activeClassName : item.className}`}
            >
              {item.title}
            </div>
          )
        })}
      </div>

      {/* 右侧内容容器 - 使用Grid布局 */}
      <div className="flex-1 p-5 grid grid-cols-1 grid-rows-1">
        {items.map((item, index) => (
          <div
            key={item.id}
            onMouseEnter={() => {
              setIsPaused(true)
            }}
            onMouseLeave={handleMouseLeave}
            className={`col-start-1 row-start-1 transition-opacity duration-${transitionDuration} ease-in-out ${
              index === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            style={{ transitionDuration: `${transitionDuration}ms` }}
          >
            {item.content}
          </div>
        ))}
      </div>
    </div>
  )
}

export default AutoSwitch
